import '@mantine/tiptap/styles.css';
import './TextEditor.css';

import { useEffect } from 'react';
import { RichTextEditor, Link } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import SubScript from '@tiptap/extension-subscript';
import Placeholder from '@tiptap/extension-placeholder';

interface HierarchicalTextEditorProps {
  content: string;
  onUpdate: (content: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: number;
}

export default function HierarchicalTextEditor({
  content,
  onUpdate,
  placeholder,
  disabled = false,
  minHeight = 200
}: HierarchicalTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Superscript,
      SubScript,
      Highlight,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: placeholder ?? 'Enter text...' }),
    ],
    content,
    onUpdate: ({ editor }) => onUpdate(editor.getHTML()),
    editable: !disabled,
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  useEffect(() => {
    if (editor && editor.extensionManager.extensions) {
      const placeholderExtension = editor.extensionManager.extensions.find(ext => ext.name === 'placeholder');
      if (placeholderExtension) {
        placeholderExtension.options.placeholder = placeholder ?? 'Enter text...';
        editor.view.updateState(editor.state);
      }
    }
  }, [placeholder, editor]);

  return (
    <RichTextEditor 
      editor={editor} 
      style={{ minHeight: `${minHeight}px` }}
    >
      <RichTextEditor.Toolbar sticky stickyOffset={60}>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Underline />
          <RichTextEditor.Strikethrough />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Subscript />
          <RichTextEditor.Superscript />
          <RichTextEditor.Highlight />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Undo />
          <RichTextEditor.Redo />
        </RichTextEditor.ControlsGroup>
      </RichTextEditor.Toolbar>
      
      <RichTextEditor.Content style={{ minHeight: `${minHeight - 60}px` }} />
    </RichTextEditor>
  );
} 